import { signal, Service } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';

@Service()
export class ThemeService {
  private readonly storageKey = 'streak-theme';
  private readonly mediaQuery = window.matchMedia(
    '(prefers-color-scheme: dark)',
  );

  public readonly preference = signal<ThemePreference>(this.readPreference());
  public readonly isDark = signal(false);

  constructor() {
    this.mediaQuery.addEventListener('change', () => {
      if (this.preference() === 'system') {
        this.applyPreference();
      }
    });
    window.addEventListener('storage', (event) => {
      if (event.key === this.storageKey) {
        this.preference.set(this.readPreference());
        this.applyPreference();
      }
    });
  }

  public init(): void {
    this.applyPreference();
  }

  public setPreference(preference: ThemePreference): void {
    if (preference === 'system') {
      localStorage.removeItem(this.storageKey);
    } else {
      localStorage.setItem(this.storageKey, preference);
    }

    this.preference.set(preference);
    this.applyPreference();
  }

  private readPreference(): ThemePreference {
    const storedPreference = localStorage.getItem(this.storageKey);
    return storedPreference === 'light' || storedPreference === 'dark'
      ? storedPreference
      : 'system';
  }

  private applyPreference(): void {
    const dark =
      this.preference() === 'dark' ||
      (this.preference() === 'system' && this.mediaQuery.matches);

    document.documentElement.classList.toggle('dark', dark);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', dark ? '#0f172a' : '#ffffff');
    this.isDark.set(dark);
  }
}
